import React from 'react';
import './BacktestAnalysis.css';

interface BacktestAnalysisProps {
  analysisData: any;
  trades: any[];
  dateRange?: string;
}

const BacktestAnalysis: React.FC<BacktestAnalysisProps> = ({ analysisData, trades, dateRange }) => {
  if (!analysisData) {
    return (
      <div className="backtest-analysis">
        <h3>Backtest Analysis</h3>
        <div className="no-data">
          No analysis data available. Run or load a backtest to see analysis.
        </div>
      </div>
    );
  }

  // Extract data from analyze-trades response
  const totalTrades = analysisData.total_trades || 0;
  const winnersAnalysis = analysisData.winners_analysis || {};
  const losersAnalysis = analysisData.losers_analysis || {};
  const profitPatternAnalysis = analysisData.profit_pattern_analysis || {};
  const byDirection = analysisData.by_direction || [];
  const byEntryReason = analysisData.by_entry_reason || [];
  const byExitReason = analysisData.by_exit_reason || [];

  // Calculate metrics
  const winningTrades = winnersAnalysis.count || 0;
  const losingTrades = losersAnalysis.count || 0;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;

  // Calculate total profit/loss from trades
  const totalProfitLoss = trades.reduce((sum, trade) => {
    const pl = trade.profitLoss !== undefined ? trade.profitLoss : trade.profit_loss;
    return sum + (pl || 0);
  }, 0);

  const avgWinnerHoldTime = winnersAnalysis.avg_hold_hours || 0;
  const avgLoserHoldTime = losersAnalysis.avg_hold_hours || 0;
  const avgWinnerRSI = winnersAnalysis.avg_rsi || 0;
  const avgLoserRSI = losersAnalysis.avg_rsi || 0;

  // Profit pattern analysis
  const greenToRed = profitPatternAnalysis.green_to_red || {};
  const alwaysRed = profitPatternAnalysis.always_red || {};
  const redToGreen = profitPatternAnalysis.red_to_green || {};
  const alwaysGreen = profitPatternAnalysis.always_green || {};

  // Extract ML analysis data
  const mlAnalysis = analysisData?.ml_analysis;

  return (
    <>
      <div className="analysis-header">
        <h3>Backtest Analysis{dateRange}</h3>
      </div>

      {/* Overview Metrics */}
      <section className="analysis-section">
        <h4>Performance Overview</h4>
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Total Trades</div>
            <div className="metric-value">{totalTrades}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Win Rate</div>
            <div className="metric-value success">{winRate.toFixed(2)}%</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Winning Trades</div>
            <div className="metric-value success">{winningTrades}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Losing Trades</div>
            <div className="metric-value error">{losingTrades}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Total P/L</div>
            <div className={`metric-value ${totalProfitLoss >= 0 ? 'success' : 'error'}`}>
              ${totalProfitLoss.toFixed(2)}
            </div>
          </div>
          {mlAnalysis && (
            <div className="metric-card">
              <div className="metric-label">Avg ML Confidence</div>
              <div className="metric-value">{(mlAnalysis.approved_trades.avg_confidence * 100).toFixed(1)}%</div>
            </div>
          )}
        </div>
      </section>

      {/* Hold Time Analysis */}
      <section className="analysis-section">
        <h4>Hold Time Analysis</h4>
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Avg Winner Hold Time</div>
            <div className="metric-value success">{avgWinnerHoldTime.toFixed(2)} hrs</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Avg Loser Hold Time</div>
            <div className="metric-value error">{avgLoserHoldTime.toFixed(2)} hrs</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Hold Time Difference</div>
            <div className="metric-value">
              {Math.abs(avgLoserHoldTime - avgWinnerHoldTime).toFixed(2)} hrs
            </div>
            <div className="metric-note">
              {avgLoserHoldTime > avgWinnerHoldTime
                ? 'Holding losers longer'
                : 'Cutting losers faster'}
            </div>
          </div>
        </div>
      </section>

      {/* RSI Analysis */}
      <section className="analysis-section">
        <h4>RSI at Entry Analysis</h4>
        <div className="metrics-grid">
          <div className="metric-card">
            <div className="metric-label">Avg RSI - Winners</div>
            <div className="metric-value success">{avgWinnerRSI.toFixed(2)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">Avg RSI - Losers</div>
            <div className="metric-value error">{avgLoserRSI.toFixed(2)}</div>
          </div>
          <div className="metric-card">
            <div className="metric-label">RSI Difference</div>
            <div className="metric-value">
              {Math.abs(avgWinnerRSI - avgLoserRSI).toFixed(2)}
            </div>
          </div>
        </div>
      </section>

      {/* Profit Pattern Analysis */}
      <section className="analysis-section">
        <h4>Profit Pattern Analysis</h4>
        <div className="pattern-grid">
          <div className="pattern-card">
            <div className="pattern-title">Green to Red</div>
            <div className="pattern-subtitle">Started winning, ended losing</div>
            <div className="pattern-stats">
              <div className="stat-item">
                <span className="stat-label">Count:</span>
                <span className="stat-value">{greenToRed.count || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">% of Losers:</span>
                <span className="stat-value">{(greenToRed.percentage_of_losers || 0).toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Max Profit Given Back:</span>
                <span className="stat-value error">${(greenToRed.avg_max_profit_given_back || 0).toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Hold Time:</span>
                <span className="stat-value">{(greenToRed.avg_hold_hours || 0).toFixed(1)} hrs</span>
              </div>
            </div>
          </div>

          <div className="pattern-card">
            <div className="pattern-title">Always Red</div>
            <div className="pattern-subtitle">Never went profitable</div>
            <div className="pattern-stats">
              <div className="stat-item">
                <span className="stat-label">Count:</span>
                <span className="stat-value">{alwaysRed.count || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">% of Losers:</span>
                <span className="stat-value">{(alwaysRed.percentage_of_losers || 0).toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Loss:</span>
                <span className="stat-value error">${(alwaysRed.avg_loss || 0).toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Hold Time:</span>
                <span className="stat-value">{(alwaysRed.avg_hold_hours || 0).toFixed(1)} hrs</span>
              </div>
            </div>
          </div>

          <div className="pattern-card">
            <div className="pattern-title">Red to Green</div>
            <div className="pattern-subtitle">Started losing, ended winning</div>
            <div className="pattern-stats">
              <div className="stat-item">
                <span className="stat-label">Count:</span>
                <span className="stat-value">{redToGreen.count || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">% of Winners:</span>
                <span className="stat-value">{(redToGreen.percentage_of_winners || 0).toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Max Drawdown Recovered:</span>
                <span className="stat-value success">${Math.abs(redToGreen.avg_max_drawdown_recovered || 0).toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Hold Time:</span>
                <span className="stat-value">{(redToGreen.avg_hold_hours || 0).toFixed(1)} hrs</span>
              </div>
            </div>
          </div>

          <div className="pattern-card">
            <div className="pattern-title">Always Green</div>
            <div className="pattern-subtitle">Never went negative</div>
            <div className="pattern-stats">
              <div className="stat-item">
                <span className="stat-label">Count:</span>
                <span className="stat-value">{alwaysGreen.count || 0}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">% of Winners:</span>
                <span className="stat-value">{(alwaysGreen.percentage_of_winners || 0).toFixed(1)}%</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Profit:</span>
                <span className="stat-value success">${(alwaysGreen.avg_profit || 0).toFixed(2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Avg Hold Time:</span>
                <span className="stat-value">{(alwaysGreen.avg_hold_hours || 0).toFixed(1)} hrs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* By Direction */}
      <section className="analysis-section">
        <h4>Performance by Direction</h4>
        <div className="table-container">
          <table className="analysis-table">
            <thead>
              <tr>
                <th>Direction</th>
                <th>Count</th>
                <th>Win Rate</th>
                <th>Avg P/L</th>
                <th>Total Profit</th>
              </tr>
            </thead>
            <tbody>
              {byDirection.map((dir: any, index: number) => {
                const count = dir.count || 0;
                const winRate = dir.win_rate || 0;
                const avgProfit = dir.avg_profit || 0;
                const totalProfit = dir.total_profit || (avgProfit * count);

                return (
                  <tr key={index}>
                    <td className={dir.direction === 'BUY' ? 'direction-buy' : 'direction-sell'}>
                      {dir.direction}
                    </td>
                    <td>{count}</td>
                    <td>{winRate.toFixed(2)}%</td>
                    <td className={avgProfit >= 0 ? 'success' : 'error'}>
                      ${avgProfit.toFixed(2)}
                    </td>
                    <td className={totalProfit >= 0 ? 'success' : 'error'}>
                      ${totalProfit.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* By Entry Reason */}
      <section className="analysis-section">
        <h4>Performance by Entry Reason</h4>
        <div className="table-container">
          <table className="analysis-table">
            <thead>
              <tr>
                <th>Entry Reason</th>
                <th>Count</th>
                <th>Win Rate</th>
                <th>Avg P/L</th>
                <th>Total Profit</th>
              </tr>
            </thead>
            <tbody>
              {byEntryReason.map((reason: any, index: number) => {
                const count = reason.count || 0;
                const winRate = reason.win_rate || 0;
                const avgProfit = reason.avg_profit || 0;
                const totalProfit = reason.total_profit || (avgProfit * count);

                return (
                  <tr key={index}>
                    <td>{reason.entry_reason || 'N/A'}</td>
                    <td>{count}</td>
                    <td>{winRate.toFixed(2)}%</td>
                    <td className={avgProfit >= 0 ? 'success' : 'error'}>
                      ${avgProfit.toFixed(2)}
                    </td>
                    <td className={totalProfit >= 0 ? 'success' : 'error'}>
                      ${totalProfit.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* By Exit Reason */}
      <section className="analysis-section">
        <h4>Performance by Exit Reason</h4>
        <div className="table-container">
          <table className="analysis-table">
            <thead>
              <tr>
                <th>Exit Reason</th>
                <th>Count</th>
                <th>Win Rate</th>
                <th>Avg P/L</th>
                <th>Total Profit</th>
              </tr>
            </thead>
            <tbody>
              {byExitReason.map((reason: any, index: number) => {
                const count = reason.count || 0;
                const winRate = reason.win_rate || 0;
                const avgProfit = reason.avg_profit || 0;
                const totalProfit = reason.total_profit || (avgProfit * count);

                return (
                  <tr key={index}>
                    <td>{reason.exit_reason || 'N/A'}</td>
                    <td>{count}</td>
                    <td>{winRate.toFixed(2)}%</td>
                    <td className={avgProfit >= 0 ? 'success' : 'error'}>
                      ${avgProfit.toFixed(2)}
                    </td>
                    <td className={totalProfit >= 0 ? 'success' : 'error'}>
                      ${totalProfit.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

export default BacktestAnalysis;
